import tw from 'twin.macro'

import React, { Fragment, useRef } from 'react'
import * as Portal from '@radix-ui/react-portal'
import { Combobox } from '@headlessui/react'
import { CgChevronDown, CgInfo } from 'react-icons/cg'

import { inputBase } from 'common/components/Input/Input'
import { usePopper } from 'common/hooks/usePopper'
import { CURRENCY_CODE } from 'common/constants/currencyConstants'
import { SuggestionContainer, SuggestionItem } from 'common/components/Suggestions/Suggestions'
import { Tooltip } from 'common/components/Tooltip/Tooltip'

function CurrencyInput({ inputRef, selectedCurrency, setSelectedCurrency, disabled, tooltipText }) {
  const comboboxBtnRef = useRef()

  const currencyOptions = Object.values(CURRENCY_CODE)

  const handleInputKeyDown = (e) => {
    const acceptedCodes = ['Enter', 'Tab', 'ArrowUp', 'ArrowDown']
    if (!acceptedCodes.includes(e.code)) {
      e.preventDefault()
    } else if (e.code === 'Enter' && !e.shiftKey) {
      comboboxBtnRef.current.click()
    }
  }

  let [popperTrigger, popperContainer] = usePopper({
    placement: 'bottom-start',
    strategy: 'fixed',
  })

  const inputWidth = inputRef?.current?.getBoundingClientRect()?.width

  return (
    <div className='mb-6'>
      <div className='flex items-center mb-1'>
        <label htmlFor='currency' className='font-semibold text-tBlack cursor-pointer'>
          Currency
        </label>
        {Boolean(tooltipText) && (
          <Tooltip content={tooltipText} triggerClasses='cursor-pointer text-gray-500 hover:text-tGreen ml-1'>
            <CgInfo className='w-4 h-4' />
          </Tooltip>
        )}
      </div>
      <Combobox
        value={selectedCurrency}
        onChange={(value) => {
          setSelectedCurrency(value)
        }}
        disabled={disabled}
      >
        <div ref={popperTrigger} className='w-full'>
          <Combobox.Button ref={comboboxBtnRef} as='div' className='relative w-full'>
            <Combobox.Input
              ref={(e) => {
                inputRef.current = e
              }}
              name='currency'
              placeholder='Enter currency'
              autoComplete='off'
              displayValue={(value) => currencyOptions.find((option) => option === value) || CURRENCY_CODE.USD}
              onKeyDown={handleInputKeyDown}
              className='text-ellipsis bg-inherit cursor-pointer caret-transparent'
              css={[inputBase, disabled && tw`bg-gray-50 cursor-not-allowed`]}
            />
            <CgChevronDown className='absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500 w-4 h-4' />
          </Combobox.Button>
        </div>
        <Portal.Root>
          <Combobox.Options className='z-[999]' ref={popperContainer} modal={false}>
            <SuggestionContainer containerStyle={{ width: inputWidth }}>
              {currencyOptions.map((option) => (
                <Combobox.Option as={Fragment} key={option} value={option}>
                  {({ selected, active }) => <SuggestionItem active={active}>{option}</SuggestionItem>}
                </Combobox.Option>
              ))}
            </SuggestionContainer>
          </Combobox.Options>
        </Portal.Root>
      </Combobox>
      {disabled && Boolean(tooltipText) && (
        <p className='mt-2 text-xs text-gray-500'>
          If you’d like to change your currency, please contact support@trybe.do
        </p>
      )}
    </div>
  )
}

export default CurrencyInput
